import type { CSSProperties, ReactElement, ReactEventHandler } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { TargetAndTransition, Transition } from 'framer-motion';
import { CimageDynamicRenderer } from './CimageDynamicRenderer';
import { CimageSkeleton } from './CimageSkeleton';
import { CimageStaticRenderer } from './CimageStaticRenderer';
import type { CimageObjectFit } from './cimage-constants';
import type { ImageDimensionModel } from './cimage-model';

interface CimageContentProps {
  alt: string;
  ariaHidden?: boolean;
  canUseOptimizedStatic: boolean;
  decoding: NonNullable<React.ImgHTMLAttributes<HTMLImageElement>['decoding']>;
  dimensionModel: ImageDimensionModel;
  draggable?: boolean;
  imageAnimate: false | TargetAndTransition;
  imageExit: TargetAndTransition;
  imageInitial: false | TargetAndTransition;
  imageStyle: CSSProperties;
  loading: NonNullable<React.ImgHTMLAttributes<HTMLImageElement>['loading']>;
  objectFit: CimageObjectFit;
  onError: ReactEventHandler<HTMLImageElement>;
  onLoad: ReactEventHandler<HTMLImageElement>;
  priority?: boolean;
  referrerPolicy?: React.HTMLAttributeReferrerPolicy;
  renderedSrc: string;
  setImageRef: (img: HTMLImageElement | null) => void;
  shouldShowSkeleton: boolean;
  skeletonRadiusValue?: number;
  sizes?: string;
  title?: string;
  transition: Transition;
  unoptimized?: boolean;
}

export function CimageContent({
  alt,
  ariaHidden,
  canUseOptimizedStatic,
  decoding,
  dimensionModel,
  draggable,
  imageAnimate,
  imageExit,
  imageInitial,
  imageStyle,
  loading,
  objectFit,
  onError,
  onLoad,
  priority,
  referrerPolicy,
  renderedSrc,
  setImageRef,
  shouldShowSkeleton,
  skeletonRadiusValue,
  sizes,
  title,
  transition,
  unoptimized,
}: CimageContentProps): ReactElement {
  return (
    <>
      <AnimatePresence>
        {shouldShowSkeleton && (
          <CimageSkeleton
            radiusValue={skeletonRadiusValue}
            transition={transition}
          />
        )}
      </AnimatePresence>
      {canUseOptimizedStatic ? (
        <CimageStaticRenderer
          alt={alt}
          ariaHidden={ariaHidden}
          dimensionModel={dimensionModel}
          draggable={draggable}
          imageAnimate={imageAnimate}
          imageExit={imageExit}
          imageInitial={imageInitial}
          imageStyle={imageStyle}
          objectFit={objectFit}
          onError={onError}
          onLoad={onLoad}
          priority={priority}
          renderedSrc={renderedSrc}
          setImageRef={setImageRef}
          sizes={sizes}
          title={title}
          transition={transition}
          unoptimized={unoptimized}
        />
      ) : (
        <CimageDynamicRenderer
          alt={alt}
          ariaHidden={ariaHidden}
          decoding={decoding}
          draggable={draggable}
          imageAnimate={imageAnimate}
          imageExit={imageExit}
          imageInitial={imageInitial}
          imageStyle={imageStyle}
          loading={loading}
          onError={onError}
          onLoad={onLoad}
          priority={priority}
          referrerPolicy={referrerPolicy}
          renderedSrc={renderedSrc}
          setImageRef={setImageRef}
          title={title}
          transition={transition}
        />
      )}
    </>
  );
}
